import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import dayjs from "dayjs";
import { api, getApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { fmtLog } from "@/lib/tz";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import {
  Newspaper, RefreshCw, Loader2, AlertTriangle, ExternalLink, ChevronDown, ChevronRight, FileText, Mail, Clock,
} from "lucide-react";

const ACCENT = "#6b4f8a";
const KIND = {
  news: { label: "News", cls: "bg-teal-50 text-teal-700 border-teal-200" },
  updated: { label: "Updated guidance", cls: "bg-violet-50 text-violet-700 border-violet-200" },
  new: { label: "New guidance", cls: "bg-violet-50 text-violet-700 border-violet-200" },
  new_file: { label: "New document", cls: "bg-violet-50 text-violet-700 border-violet-200" },
  removed: { label: "Removed page", cls: "bg-slate-50 text-slate-600 border-slate-200" },
};
const IMPORTANCE = {
  high: { label: "Action may be needed", cls: "text-brand" },
  medium: { label: "Worth knowing", cls: "text-amber-700" },
  low: { label: "For information", cls: "text-slate-500" },
};
// Commission dates are calendar dates from their website (not system timestamps).
const fmtDay = (d) => (d ? dayjs(d).format("D MMM YYYY") : "");

function Kind({ kind }) {
  const k = KIND[kind] || { label: kind, cls: "bg-slate-50 text-slate-600 border-slate-200" };
  return <span className={`text-[10px] font-semibold uppercase tracking-wide border rounded-full px-2 py-0.5 ${k.cls}`}>{k.label}</span>;
}

function Stat({ label, value }) {
  return (
    <div className="bg-slate-50 rounded-lg px-3 py-2">
      <div className="text-[10px] font-semibold uppercase tracking-wide text-slate-400">{label}</div>
      <div className="text-sm font-semibold text-slate-800 mt-0.5">{value}</div>
    </div>
  );
}

function DigestCard({ d, open, onToggle }) {
  return (
    <div className="border border-slate-200 rounded-lg" data-testid={`ndis-digest-${d.id}`}>
      <button className="w-full flex items-center gap-2 px-3 py-2.5 text-left" onClick={onToggle}>
        {open ? <ChevronDown className="h-4 w-4 text-slate-400" /> : <ChevronRight className="h-4 w-4 text-slate-400" />}
        <span className="text-sm font-medium text-slate-800 flex-1">Update for the {d.period}</span>
        <span className="text-[11px] text-slate-500">{d.items.length} item{d.items.length !== 1 ? "s" : ""} · emailed to {d.recipients} · {fmtLog(d.at)}</span>
      </button>
      {open && (
        <div className="px-4 pb-4 space-y-3">
          {d.overview && <p className="text-sm text-slate-700">{d.overview}</p>}
          {!d.ai && <p className="text-[11px] text-amber-700">The AI summary wasn't available this week, so only the list of changes was sent.</p>}
          {d.items.map((it) => (
            <div key={it.ref} className="border-l-2 pl-3" style={{ borderColor: ACCENT }}>
              <div className="flex items-center gap-2 flex-wrap">
                <Kind kind={it.kind} />
                {it.date && <span className="text-[11px] text-slate-400">{fmtDay(it.date)}</span>}
                <span className={`text-[11px] font-medium ${(IMPORTANCE[it.importance] || IMPORTANCE.medium).cls}`}>
                  {(IMPORTANCE[it.importance] || IMPORTANCE.medium).label}
                </span>
              </div>
              <div className="text-sm font-medium text-slate-800 mt-1">
                {it.kind === "removed" ? it.title : (
                  <a href={it.url} target="_blank" rel="noopener noreferrer" className="hover:underline inline-flex items-center gap-1">
                    {it.title}<ExternalLink className="h-3 w-3 text-slate-400" />
                  </a>
                )}
              </div>
              {it.page_title && <div className="text-[11px] text-slate-500">In: {it.page_title}</div>}
              <p className="text-xs text-slate-600 mt-1">{it.summary}</p>
              {it.actions?.length > 0 && (
                <ul className="list-disc ml-4 mt-1 text-xs text-slate-600">{it.actions.map((a, i) => <li key={i}>{a}</li>)}</ul>
              )}
              {it.policies?.length > 0 && (
                <div className="text-xs text-slate-600 mt-1 flex flex-wrap items-center gap-1.5">
                  <span className="font-medium">May affect:</span>
                  {it.policies.map((p) => (
                    <Link key={p.document_id} to={`/documents/${p.library}/${p.document_id}`}
                      className="inline-flex items-center gap-1 text-[#6b4f8a] hover:underline">
                      <FileText className="h-3 w-3" />{p.label}
                    </Link>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default function NdisCommission() {
  const { can } = useAuth();
  const isAdmin = can.has("docs.admin");
  const [status, setStatus] = useState(null);
  const [changes, setChanges] = useState([]);
  const [digests, setDigests] = useState(null);
  const [openDigest, setOpenDigest] = useState(null);
  const [showFailures, setShowFailures] = useState(false);
  const [starting, setStarting] = useState(false);

  const load = useCallback(() => Promise.all([
    api.govNdisStatus().then(setStatus),
    api.govNdisChanges().then((r) => setChanges(r.changes)),
    api.govNdisDigests().then((r) => { setDigests(r.digests); setOpenDigest((o) => o ?? r.digests[0]?.id ?? null); }),
  ]).catch((e) => toast.error(getApiError(e, "Couldn't load NDIS Commission updates."))), []);
  useEffect(() => { load(); }, [load]);

  // While a check runs, refresh every few seconds.
  useEffect(() => {
    if (!status?.running) return undefined;
    const t = setInterval(load, 5000);
    return () => clearInterval(t);
  }, [status?.running, load]);

  const checkNow = async () => {
    setStarting(true);
    try {
      await api.govNdisSync();
      toast.success("Checking the NDIS Commission website. This can take several minutes.");
      setStatus((s) => ({ ...s, running: true }));
    } catch (e) { toast.error(getApiError(e, "Couldn't start the check.")); }
    finally { setStarting(false); }
  };

  const last = status?.last_result;
  return (
    <div className="p-6 max-w-5xl mx-auto" data-testid="ndis-commission">
      <div className="flex items-center gap-2.5">
        <Newspaper className="h-5 w-5" style={{ color: ACCENT }} />
        <h1 className="text-xl font-semibold font-brand text-slate-800">NDIS Commission updates</h1>
      </div>
      <p className="text-xs text-slate-500 mt-1 mb-5">
        Every week the app checks the NDIS Quality and Safeguards Commission website for new or changed guidance and news.
        The Policies AI and AI review use this guidance (always labelled as Commission guidance, with a link), and the
        Management Team and Executive Team are emailed a summary.
      </p>

      <div className="bg-white border border-slate-200 rounded-xl p-4">
        <div className="flex items-center justify-between gap-2 mb-3">
          <div className="flex items-center gap-2">
            <Clock className="h-4 w-4" style={{ color: ACCENT }} />
            <h2 className="text-[11px] font-bold uppercase tracking-wider text-slate-500">Website check</h2>
          </div>
          {isAdmin && (
            <Button size="sm" className="h-8 text-xs" style={{ backgroundColor: ACCENT }} onClick={checkNow}
              disabled={starting || status?.running} data-testid="ndis-sync-now">
              {status?.running || starting ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5 mr-1" />}
              {status?.running ? "Checking…" : "Check now"}
            </Button>
          )}
        </div>
        {status === null ? (
          <div className="flex justify-center py-6"><Loader2 className="h-5 w-5 animate-spin text-slate-400" /></div>
        ) : !status.last_run_at ? (
          <div className="text-xs text-slate-500 py-4 text-center border border-dashed border-slate-200 rounded-lg">
            {status.running ? "The first check is running. It reads every guidance page and document, so it can take several minutes."
              : "The website hasn't been checked yet. The first check builds the guidance index; nothing is reported as changed until the following week."}
          </div>
        ) : (
          <>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
              <Stat label="Last checked" value={fmtLog(status.last_run_at)} />
              <Stat label="Guidance pages" value={status.pages} />
              <Stat label="Guidance documents" value={status.files} />
              <Stat label="Searchable sections" value={status.sections} />
            </div>
            {last?.error && <div className="text-xs text-rose-600 mt-3">{last.error}</div>}
            {status.without_embeddings > 0 && (
              <div className="text-[11px] text-amber-700 mt-2">{status.without_embeddings} sections couldn't be prepared for search by meaning; keyword search still works for them.</div>
            )}
            {status.failures.length > 0 && (
              <div className="mt-3">
                <button className="flex items-center gap-1.5 text-xs text-amber-700 hover:underline" onClick={() => setShowFailures((v) => !v)} data-testid="ndis-failures-toggle">
                  <AlertTriangle className="h-3.5 w-3.5" />
                  {status.failures.length} page{status.failures.length !== 1 ? "s" : ""} or document{status.failures.length !== 1 ? "s" : ""} couldn't be read. The last good copy is kept and they're retried next time.
                </button>
                {showFailures && (
                  <ul className="mt-2 space-y-1">
                    {status.failures.map((f) => (
                      <li key={f.url} className="text-[11px] text-slate-600">
                        <a href={f.url} target="_blank" rel="noopener noreferrer" className="hover:underline">{f.title || f.url}</a>
                        <span className="text-slate-400"> · {f.error}{f.at ? ` · ${fmtLog(f.at)}` : ""}</span>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            )}
          </>
        )}
      </div>

      <div className="bg-white border border-slate-200 rounded-xl p-4 mt-5">
        <div className="flex items-center gap-2 mb-3">
          <Newspaper className="h-4 w-4" style={{ color: ACCENT }} />
          <h2 className="text-[11px] font-bold uppercase tracking-wider text-slate-500">Found since the last email</h2>
        </div>
        {!changes.length ? (
          <div className="text-xs text-slate-400 py-4 text-center border border-dashed border-slate-200 rounded-lg">Nothing new yet. Changes found are emailed in the next weekly update.</div>
        ) : (
          <div className="divide-y divide-slate-100" data-testid="ndis-changes">
            {changes.map((c) => (
              <div key={c.id} className="py-2 flex items-center gap-2 flex-wrap">
                <Kind kind={c.kind} />
                {c.kind === "removed" ? <span className="text-sm text-slate-700">{c.title}</span> : (
                  <a href={c.url} target="_blank" rel="noopener noreferrer" className="text-sm text-slate-800 hover:underline inline-flex items-center gap-1">
                    {c.title}<ExternalLink className="h-3 w-3 text-slate-400" />
                  </a>
                )}
                {c.date && <span className="text-[11px] text-slate-400">{fmtDay(c.date)}</span>}
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="bg-white border border-slate-200 rounded-xl p-4 mt-5">
        <div className="flex items-center gap-2 mb-3">
          <Mail className="h-4 w-4" style={{ color: ACCENT }} />
          <h2 className="text-[11px] font-bold uppercase tracking-wider text-slate-500">Weekly updates sent</h2>
        </div>
        {digests === null ? (
          <div className="flex justify-center py-6"><Loader2 className="h-5 w-5 animate-spin text-slate-400" /></div>
        ) : !digests.length ? (
          <div className="text-xs text-slate-400 py-4 text-center border border-dashed border-slate-200 rounded-lg">No weekly updates have been sent yet.</div>
        ) : (
          <div className="space-y-2">
            {digests.map((d) => (
              <DigestCard key={d.id} d={d} open={openDigest === d.id} onToggle={() => setOpenDigest((o) => (o === d.id ? null : d.id))} />
            ))}
          </div>
        )}
      </div>

      <p className="text-[11px] text-slate-400 mt-4">
        Summaries are written by AI and may miss detail: check the Commission's page before acting. Source material
        © Commonwealth of Australia (NDIS Quality and Safeguards Commission), CC BY 4.0 AU.
      </p>
    </div>
  );
}
